import { useWalletBreakdown } from '../hooks/useWalletBreakdown';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  Bitcoin,
  DollarSign,
  CreditCard,
  Banknote,
  AlertTriangle,
  CheckCircle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  Shield,
  Info,
  Layers,
  CreditCard as Card,
  ArrowRight,
  Landmark
} from 'lucide-react';
import NowPaymentsDeposit from './NowPaymentsDeposit';
import BankWithdrawalModal from './BankWithdrawalModal';
import CryptoWithdrawalModal from './CryptoWithdrawalModal';
import { Transaction } from '../App';
import CryptoHoldings from './CryptoHoldings';
import { MarketData, DatabaseUserAsset } from '../hooks/useDatabase';
import { useDatabase } from '../hooks/useDatabase';
import { supabase } from '../lib/supabaseClient';
import WalletBreakdownCard from './WalletBreakdownCard';
import { DASHBOARD_PATHS } from '../routes/dashboardRoutes';

interface WalletBreakdown {
  totalBalance: number;
  usedMargin: number;
  unrealizedPnl: number;
  availableBalance: number;
  reserved: number;
}

interface WalletPageProps {
  usdtBalance: number;
  btcBalance: number;
  transactions: Transaction[];
  kycStatus?: 'not_verified' | 'pending' | 'verified';
  marketData: MarketData[];
  userAssets?: DatabaseUserAsset[];
  onWalletOperation?: (type: string, currency: string, amount: number) => Promise<void>;
  totalPortfolioValue: number;
  updateBalances?: (balances: any) => Promise<void>;
  addTransaction?: (transaction: any) => Promise<void>;
  setShowWithdrawalModal?: (show: boolean) => void;
  showWithdrawalModal?: boolean;
  walletBreakdown?: WalletBreakdown;
  currentBtcPrice?: number;
  fetchTransactions?: () => Promise<void>;
}

const WalletPage: React.FC<WalletPageProps> = ({
  usdtBalance,
  btcBalance,
  transactions: propTransactions,
  kycStatus: propKycStatus,
  marketData,
  userAssets = [],
  onWalletOperation,
  totalPortfolioValue,
  setShowWithdrawalModal,
  showWithdrawalModal,
  currentBtcPrice,
  walletBreakdown,
  fetchTransactions: propFetchTransactions
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    userStakes,
    calculateCurrentEarnings,
    cancelUserStake,
    robotState,
    transactions: dbTransactions,
    addTransaction,
    updateBalances,
    fetchTransactions: dbFetchTransactions,
    kycStatus: dbKycStatus
  } = useDatabase();

  const transactions = propTransactions?.length > 0 ? propTransactions : dbTransactions;
  const kycStatus = propKycStatus || dbKycStatus || 'not_verified';
  const refreshTransactions = propFetchTransactions || dbFetchTransactions;

  // Helper function to format date safely
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };
  // State for wallet operations
  const [activeTab, setActiveTab] = useState('overview');
  const [depositMethod, setDepositMethod] = useState<'bank_transfer' | 'nowpayments' | 'fiat'>('fiat');
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<'USDT' | 'BTC'>('USDT');
  const [showBalance, setShowBalance] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [showBankWithdrawalModal, setShowBankWithdrawalModal] = useState(false);
  const [showCryptoWithdrawalModal, setShowCryptoWithdrawalModal] = useState(false);
  const [selectedCryptoForWithdrawal, setSelectedCryptoForWithdrawal] = useState<'USDT' | 'BTC'>('USDT');
  
  // Use the wallet breakdown hook
  const walletBreakdownData = useWalletBreakdown(
    usdtBalance,
    btcBalance,
    currentBtcPrice || 0,
    robotState,
    userAssets,
    marketData
  );
  
  // State for bank transfer details
  const [bankDetails, setBankDetails] = useState<any>(null);
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Withdrawal limit for unverified users (200 USD)
  const UNVERIFIED_WITHDRAWAL_LIMIT = 200;

  // Get user ID from Supabase auth
  const [userId, setUserId] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    
    fetchUserId();
  }, []);

  // Fetch bank details when bank transfer tab is active
  const fetchBankDetails = useCallback(async () => {
    if (!userId) return;
    setLoadingBankDetails(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('client_bank_details')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        throw error;
      }
      setBankDetails(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load bank details.');
      setBankDetails(null);
    } finally {
      setLoadingBankDetails(false);
    }
  }, [userId]);

  useEffect(() => {
    if (activeTab === 'deposit' && depositMethod === 'bank_transfer') {
      fetchBankDetails();
    }
  }, [activeTab, depositMethod, fetchBankDetails]);

  // Handle crypto withdrawal button click
  const handleCryptoWithdrawalClick = (currency: 'USDT' | 'BTC') => {
    setSelectedCryptoForWithdrawal(currency);
    setShowCryptoWithdrawalModal(true);
  };

  // Handle withdrawal submission
  const handleBankWithdrawalSubmit = async (amount: number, bankDetails: {
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    beneficiaryName: string;
  }): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const fee = amount * 0.005;
      const netAmount = amount - fee;

      const { data: txData, error: transactionError } = await supabase
        .from('transactions')
        .insert([{
          user_id: user.id,
          type: 'withdrawal',
          amount: -amount,
          description: `Bank withdrawal of ${amount} USDT to ${bankDetails.bankName} (Acc: ••••${bankDetails.accountNumber.slice(-4)})`,
          status: 'pending'
        }])
        .select('id')
        .maybeSingle();

      if (transactionError) {
        throw new Error(`Failed to create withdrawal transaction: ${transactionError.message}`);
      }

      await supabase.from('withdrawals').insert([{
        user_id: user.id,
        transaction_id: txData?.id || null,
        withdrawal_method: 'bank',
        currency: 'USDT',
        amount,
        fee,
        net_amount: netAmount,
        status: 'pending',
        bank_name: bankDetails.bankName,
        account_number: bankDetails.accountNumber,
        routing_number: bankDetails.routingNumber,
        beneficiary_name: bankDetails.beneficiaryName
      }]);

      await refreshTransactions();
      setMessage({ type: 'success', text: 'Bank withdrawal initiated successfully' });
      setShowBankWithdrawalModal(false);

      return true;
    } catch (error) {
      console.error('Error processing bank withdrawal:', error);
      setMessage({ type: 'error', text: 'Withdrawal failed. Please try again.' });
      return false;
    }
  };

  // Handle crypto withdrawal
  const handleCryptoWithdrawal = async (currency: 'USDT' | 'BTC', amount: number, address: string, network: string): Promise<boolean> => {
    try {
      const withdrawalValueUSD = currency === 'USDT' ? amount : amount * (currentBtcPrice || 0);

      if (walletBreakdownData && withdrawalValueUSD > walletBreakdownData.availableBalance) {
        throw new Error(`Insufficient available balance. Required: ${withdrawalValueUSD.toFixed(2)} USD, Available: ${walletBreakdownData.availableBalance.toFixed(2)} USD`);
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const fee = currency === 'USDT' ? amount * 0.001 : 0.0005;
      const netAmount = amount - fee;

      const { data: txData, error: transactionError } = await supabase
        .from('transactions')
        .insert([{
          user_id: user.id,
          type: 'withdrawal',
          amount: -amount,
          description: `${currency} withdrawal of ${amount} ${currency} to ${address.substring(0, 8)}...${address.substring(address.length - 8)}`,
          status: 'pending'
        }])
        .select('id')
        .maybeSingle();

      if (transactionError) {
        throw new Error(`Failed to create withdrawal transaction: ${transactionError.message}`);
      }

      await supabase.from('withdrawals').insert([{
        user_id: user.id,
        transaction_id: txData?.id || null,
        withdrawal_method: 'crypto',
        currency,
        amount,
        fee,
        net_amount: netAmount,
        status: 'pending',
        wallet_address: address,
        network
      }]);

      await refreshTransactions();
      setMessage({ type: 'success', text: 'Crypto withdrawal initiated successfully' });
      setShowCryptoWithdrawalModal(false);

      return true;
    } catch (error) {
      console.error('Error processing crypto withdrawal:', error);
      setMessage({ type: 'error', text: 'Withdrawal failed. Please try again.' });
      return false;
    }
  };

  const totalStakedValue = userStakes.reduce((total, stake) => {
    if (stake.status !== 'active') return total;
    
    const stakedValue = stake.asset_symbol === 'USDT' 
      ? parseFloat(stake.staked_amount.toString())
      : parseFloat(stake.staked_amount.toString()) * (marketData.find(m => m.symbol === 'BTCUSDT')?.price || 0);
    
    return total + stakedValue;
  }, 0);

  // Calculate total earnings from staking
  const totalStakingEarnings = userStakes.reduce((total, stake) => {
    const earnings = calculateCurrentEarnings(stake);
    return total + earnings;
  }, 0);

  // Calculate 24h change
  const btc24hChange = marketData.find(m => m.symbol === 'BTCUSDT')?.change_24h || 0;
  const portfolioChange24h = (btcBalance * (currentBtcPrice || 0) * (btc24hChange / 100));
  
  // Use the wallet breakdown data for consistent calculations
  const effectiveTotalPortfolioValue = walletBreakdownData.totalBalance;
  const effectiveAvailableBalance = walletBreakdownData.availableBalance;

  // Handle stake cancellation
  const handleCancelStake = async (stakeId: string) => {
    try {
      await cancelUserStake(stakeId);
    } catch (error) {
      console.error('Error cancelling stake:', error);
    }
  };

  // Calculate total available balance from all user assets
  const totalAvailableBalance = useMemo(() => {
    // Use the wallet breakdown available balance for consistency
    return walletBreakdownData?.availableBalance || 0;
  }, [walletBreakdownData?.availableBalance]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatCrypto = (amount: number, symbol: string) => {
    const decimals = symbol === 'BTC' ? 6 : 2;
    return `${amount.toFixed(decimals)} ${symbol}`;
  };

  const getTransactionTypeLabel = (type: string) => {
    switch (type) {
      case 'deposit': return t('wallet.transactionTypes.deposit');
      case 'withdrawal': return t('wallet.transactionTypes.withdrawal');
      case 'trade': return t('wallet.transactionTypes.trade');
      case 'robot_profit': return t('wallet.transactionTypes.robotProfit');
      case 'binary_trade': return t('wallet.transactionTypes.binaryTrade');
      case 'stake': return t('wallet.transactionTypes.stake');
      case 'staking_profit': return t('wallet.transactionTypes.stakingProfit');
      case 'staking_return': return t('wallet.transactionTypes.stakingReturn');
      case 'challenge_fee': return t('wallet.transactionTypes.challengeFee');
      case 'challenge_reward': return t('wallet.transactionTypes.challengeReward');
      default: return type;
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
      case 'nowpayments_deposit':
        return <ArrowDownLeft size={16} className="text-green-400" />;
      case 'withdrawal':
        return <ArrowUpRight size={16} className="text-red-400" />;
      case 'trade':
        return <TrendingUp size={16} className="text-blue-400" />;
      case 'robot_profit':
        return <TrendingUp size={16} className="text-purple-400" />;
      case 'binary_trade':
        return <TrendingUp size={16} className="text-orange-400" />;
      case 'stake':
        return <Layers size={16} className="text-indigo-400" />;
      case 'staking_profit':
        return <TrendingUp size={16} className="text-green-400" />;
      case 'staking_return':
        return <ArrowDownLeft size={16} className="text-blue-400" />;
      default:
        return <DollarSign size={16} className="text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'pending':
        return 'text-yellow-400';
      case 'failed':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={14} className="text-green-400" />;
      case 'pending':
        return <Clock size={14} className="text-yellow-400" />;
      case 'failed':
        return <AlertTriangle size={14} className="text-red-400" />;
      default:
        return <Clock size={14} className="text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/95">
      {/* Message Display */}
      {message && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg border"
          style={{
            background: message.type === 'success'
              ? 'rgba(34, 197, 94, 0.1)'
              : message.type === 'error'
              ? 'rgba(239, 68, 68, 0.1)'
              : 'rgba(245, 158, 11, 0.1)',
            border: message.type === 'success'
              ? '1px solid rgba(34, 197, 94, 0.3)'
              : message.type === 'error'
              ? '1px solid rgba(239, 68, 68, 0.3)'
              : '1px solid rgba(245, 158, 11, 0.3)',
            color: message.type === 'success'
              ? '#22c55e'
              : message.type === 'error'
              ? '#ef4444'
              : '#f59e0b'
          }}>
          <div className="flex items-center gap-2">
            {message.type === 'success' && <CheckCircle size={18} />}
            {message.type === 'error' && <AlertTriangle size={18} />}
            {message.type === 'warning' && <AlertTriangle size={18} />}
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{
              background: 'linear-gradient(135deg, #3b82f6, #a855f7)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
            }}>
              <Wallet size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{
                background: 'linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {t('wallet.title')}
              </h1>
              <p style={{ color: '#64748b' }}>
                {t('wallet.subtitle')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/trading-fees')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors border text-sm"
              style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                color: '#3b82f6'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
                e.currentTarget.style.color = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                e.currentTarget.style.color = '#3b82f6';
              }}
            >
              <Info size={18} />
              <span>Trading Fees</span>
            </button>

            <button
              onClick={() => setShowBalance(!showBalance)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors border text-sm"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                color: '#64748b'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.color = '#475569';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              {showBalance ? <Eye size={18} /> : <EyeOff size={18} />}
              <span>{showBalance ? 'Hide' : 'Show'} Balance</span>
            </button>
          </div>
        </div>

        {/* Portfolio Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Total Portfolio Value */}
          <div className="lg:col-span-2 rounded-2xl p-6 border shadow-2xl h-full flex flex-col" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: '#475569' }}>Total Portfolio Value</h3>
              <div className="flex items-center gap-2">
                {portfolioChange24h >= 0 ? (
                  <TrendingUp size={18} className="text-green-400" />
                ) : (
                  <TrendingDown size={18} className="text-red-400" />
                )}
                <span className={`text-sm font-medium ${portfolioChange24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {portfolioChange24h >= 0 ? '+' : ''}{formatCurrency(portfolioChange24h)}
                </span>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="text-4xl font-bold mb-2" style={{ color: '#1e293b' }}>
                {showBalance ? formatCurrency(walletBreakdownData?.totalBalance || 0) : '••••••'}
              </div>
              <div className="text-sm" style={{ color: '#64748b' }}>
                Available Balance: {showBalance ? formatCurrency(walletBreakdownData?.availableBalance || 0) : '••••••'}
              </div>
            </div>

            {/* Asset Breakdown */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl p-4 border" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)'
                  }}>
                    <span className="text-xs font-bold text-white">$</span>
                  </div>
                  <span className="font-medium" style={{ color: '#475569' }}>USDT</span>
                </div>
                <div className="text-xl font-bold" style={{ color: '#1e293b' }}>
                  {showBalance ? formatCrypto(usdtBalance, 'USDT') : '••••••'}
                </div>
                <div className="text-sm" style={{ color: '#64748b' }}>
                  {showBalance ? formatCurrency(usdtBalance) : '••••••'}
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <button
                    onClick={() => setActiveTab('deposit')}
                    className="w-full px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 shadow-lg flex items-center justify-center gap-1 md:gap-2"
                    style={{
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.25)',
                      color: '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    <ArrowDownLeft size={16} />
                    Deposit
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCryptoWithdrawalClick('USDT')}
                      className="flex-1 px-2 py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 shadow-lg flex items-center justify-center gap-1"
                      style={{
                        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                        color: '#ffffff',
                        cursor: 'pointer'
                      }}>
                        <Bitcoin size={14} />
                        Crypto
                      </button>
                    <button
                      onClick={() => setShowBankWithdrawalModal(true)}
                      className="flex-1 px-2 py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 shadow-lg flex items-center justify-center gap-1"
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                        color: '#ffffff',
                        cursor: 'pointer'
                      }}>
                        <Landmark size={14} />
                        Bank
                      </button>
                  </div>
                </div>
              </div>

              <div className="rounded-xl p-4 border" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                <div className="flex items-center gap-3 mb-2">
                  <Bitcoin size={20} style={{ color: '#f97316' }} />
                  <span className="font-medium" style={{ color: '#475569' }}>BTC</span>
                </div>
                <div className="text-xl font-bold" style={{ color: '#1e293b' }}>
                  {showBalance ? formatCrypto(btcBalance, 'BTC') : '••••••'}
                </div>
                <div className="text-sm" style={{ color: '#64748b' }}>
                  {showBalance ? formatCurrency(btcBalance * (currentBtcPrice || 0)) : '••••••'}
                </div>
                <div className="flex flex-col md:flex-row gap-3 mt-4">
                  <button
                    onClick={() => setActiveTab('deposit')}
                    className="flex-1 px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 shadow-lg flex items-center justify-center gap-1 md:gap-2"
                    style={{
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.25)',
                      color: '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    <ArrowDownLeft size={16} />
                    Deposit
                  </button>
                  <button
                    onClick={() => handleCryptoWithdrawalClick('BTC')}
                    className="flex-1 px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 shadow-lg flex items-center justify-center gap-1 md:gap-2"
                    style={{
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                      color: '#ffffff',
                      cursor: 'pointer'
                    }}>
                      <ArrowUpRight size={16} />
                      Withdraw
                    </button>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Breakdown */}
          <div className="h-full">
            {walletBreakdownData && !walletBreakdownData.loading ? (
              <WalletBreakdownCard
                totalBalance={walletBreakdownData.totalBalance}
                usedMargin={walletBreakdownData.usedMargin}
                futuresUsedMargin={walletBreakdownData.futuresUsedMargin}
                propUsedMargin={walletBreakdownData.propUsedMargin}
                futuresOrdersReserved={walletBreakdownData.futuresOrdersReserved}
                propOrdersReserved={walletBreakdownData.propOrdersReserved}
                unrealizedPnl={walletBreakdownData.unrealizedPnl}
                availableBalance={walletBreakdownData.availableBalance}
                robotAllocatedBalance={walletBreakdownData.robotAllocatedBalance}
                stakedAmount={walletBreakdownData.stakedAmount}
                loading={walletBreakdownData.loading}
              />
            ) : (
              <div className="rounded-2xl p-6 border shadow-2xl h-full" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg" style={{
                    background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                  }}>
                    <Wallet size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>Wallet Breakdown</h3>
                </div>
                <div className="animate-pulse space-y-3">
                  <div className="h-4 rounded w-3/4" style={{ background: 'rgba(0, 0, 0, 0.1)' }}></div>
                  <div className="h-4 rounded w-1/2" style={{ background: 'rgba(0, 0, 0, 0.1)' }}></div>
                  <div className="h-4 rounded w-2/3" style={{ background: 'rgba(0, 0, 0, 0.1)' }}></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 p-2 rounded-2xl border" style={{
          background: 'rgba(0, 0, 0, 0.03)',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          {[
            { id: 'overview', label: 'Overview', icon: Wallet },
            { id: 'deposit', label: 'Deposit', icon: ArrowDownLeft },
            { id: 'withdraw', label: 'Withdraw', icon: ArrowUpRight },
            { id: 'transactions', label: 'Transactions', icon: Clock },
            { id: 'staking', label: 'Staking', icon: Layers },
            { id: 'holdings', label: 'Holdings', icon: TrendingUp }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-white"
              style={{
                background: activeTab === id
                  ? 'linear-gradient(135deg, #3b82f6, #a855f7)'
                  : 'transparent',
                color: activeTab === id ? '#ffffff' : '#64748b',
                boxShadow: activeTab === id ? '0 4px 12px rgba(59, 130, 246, 0.25)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== id) {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.color = '#475569';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Transactions */}
            <div className="rounded-2xl p-6 shadow-lg" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(16, 185, 129, 0.15))',
                    border: '2px solid rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Clock size={20} style={{ color: '#3b82f6' }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#1e293b' }}>Recent Transactions</h3>
                </div>
                <button
                  onClick={() => setActiveTab('transactions')}
                  className="text-sm font-medium flex items-center gap-1 transition-colors"
                  style={{ color: '#3b82f6' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
                >
                  View All
                  <ArrowRight size={14} />
                </button>
              </div>
              
              <div className="space-y-3">
                {transactions.slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-3 rounded-xl transition-all" style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.06)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}>
                    <div className="flex items-center gap-3">
                      {getTransactionIcon(transaction.type)}
                      <div>
                        <div className="font-medium text-sm" style={{ color: '#1e293b' }}>
                          {transaction.description}
                        </div>
                        <div className="text-xs" style={{ color: '#64748b' }}>
                          {formatDate(transaction.created_at || transaction.timestamp)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-sm" style={{
                        color: transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit'
                          ? '#22c55e'
                          : transaction.type === 'withdrawal'
                          ? '#ef4444'
                          : '#1e293b'
                      }}>
                        {transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit' ? '+' : ''}
                        {formatCurrency(parseFloat(transaction.amount.toString()))}
                      </div>
                      <div className="flex items-center gap-1 justify-end">
                        {getStatusIcon(transaction.status)}
                        <span className={`text-xs ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                {transactions.length === 0 && (
                  <div className="text-center py-8" style={{ color: '#64748b' }}>
                    <Clock size={48} className="mx-auto mb-4" style={{ opacity: 0.5, color: '#94a3b8' }} />
                    <p>No transactions yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Portfolio Performance */}
            <div className="rounded-2xl p-6 shadow-lg" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}>
              <div className="flex items-center gap-3 mb-6">
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(139, 92, 246, 0.15))',
                  border: '2px solid rgba(168, 85, 247, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TrendingUp size={20} style={{ color: '#a855f7' }} />
                </div>
                <h3 className="text-xl font-bold" style={{ color: '#1e293b' }}>Portfolio Performance</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 rounded-xl" style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.06)'
                }}>
                  <span style={{ color: '#64748b' }}>Total Value</span>
                  <span className="font-bold" style={{ color: '#1e293b' }}>
                    {showBalance ? formatCurrency(walletBreakdownData?.totalBalance || 0) : '••••••'}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 rounded-xl" style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.06)'
                }}>
                  <span style={{ color: '#64748b' }}>Available Balance</span>
                  <span className="font-bold" style={{ color: '#1e293b' }}>
                    {showBalance ? formatCurrency(walletBreakdownData?.availableBalance || 0) : '••••••'}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 rounded-xl" style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.06)'
                }}>
                  <span style={{ color: '#64748b' }}>Staked Assets</span>
                  <span className="font-bold" style={{ color: '#1e293b' }}>
                    {showBalance ? formatCurrency(walletBreakdownData.stakedAmount) : '••••••'}
                  </span>
                </div>

                <div className="flex justify-between items-center p-4 rounded-xl" style={{
                  background: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.06)'
                }}>
                  <span style={{ color: '#64748b' }}>Staking Earnings</span>
                  <span className="font-bold" style={{ color: '#22c55e' }}>
                    {showBalance ? `+${formatCurrency(totalStakingEarnings)}` : '••••••'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'deposit' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="rounded-2xl p-8 border shadow-2xl" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg" style={{
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.25)'
                  }}>
                    <ArrowDownLeft size={20} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Deposit Funds</h3>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm mb-3" style={{ color: '#64748b' }}>Select Currency</label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setSelectedCurrency('USDT')}
                        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          selectedCurrency === 'USDT' && depositMethod === 'bank_transfer'
                            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-500/25 transform scale-105'
                            : 'border-2'
                        }`}
                        style={selectedCurrency !== 'USDT' || depositMethod !== 'bank_transfer' ? {
                          background: 'rgba(0, 0, 0, 0.02)',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          color: '#64748b'
                        } : {}}
                      >
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-white">$</span>
                        </div>
                        USDT
                      </button>
                      <button
                        onClick={() => setSelectedCurrency('BTC')}
                        className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          selectedCurrency === 'BTC' && depositMethod === 'bank_transfer'
                            ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 transform scale-105'
                            : 'border-2'
                        }`}
                        style={selectedCurrency !== 'BTC' || depositMethod !== 'bank_transfer' ? {
                          background: 'rgba(0, 0, 0, 0.02)',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          color: '#64748b'
                        } : {}}
                      >
                        <Bitcoin size={18} className="text-orange-400" />
                        BTC
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-2" style={{ color: '#64748b' }}>
                      <span>Amount</span>
                      <span>Min: $10</span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        placeholder={`Enter ${selectedCurrency} amount`}
                        className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 transition-all"
                        style={{
                          background: 'rgba(0, 0, 0, 0.02)',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          color: '#1e293b'
                        }}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 px-2 py-1 rounded text-sm" style={{
                        background: 'rgba(0, 0, 0, 0.05)',
                        color: '#64748b'
                      }}>
                        {selectedCurrency}
                      </div>
                    </div>
                  </div>

                  {/* Deposit Method Selector */}
                  <div className="mb-6">
                    <label className="block text-sm mb-3" style={{ color: '#64748b' }}>Select Deposit Method</label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => setDepositMethod('fiat')}
                        className={`py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          depositMethod === 'fiat'
                            ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25 transform scale-105'
                            : 'border-2'
                        }`}
                        style={depositMethod !== 'fiat' ? {
                          background: 'rgba(0, 0, 0, 0.02)',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          color: '#64748b'
                        } : {}}
                      >
                        <DollarSign size={18} className={depositMethod === 'fiat' ? 'text-white' : ''} style={depositMethod !== 'fiat' ? { color: '#64748b' } : {}} />
                        Fiat
                      </button>
                      <button
                        onClick={() => setDepositMethod('nowpayments')}
                        className={`py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          depositMethod === 'nowpayments'
                            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25 transform scale-105'
                            : 'border-2'
                        }`}
                        style={depositMethod !== 'nowpayments' ? {
                          background: 'rgba(0, 0, 0, 0.02)',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          color: '#64748b'
                        } : {}}
                      >
                        <Bitcoin size={18} className={depositMethod === 'nowpayments' ? 'text-white' : ''} style={depositMethod !== 'nowpayments' ? { color: '#64748b' } : {}} />
                        Crypto
                      </button>
                      <button
                        onClick={() => setDepositMethod('bank_transfer')}
                        className={`py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                          depositMethod === 'bank_transfer'
                            ? 'bg-gradient-to-r from-slate-500 to-slate-600 text-white shadow-lg shadow-slate-500/25 transform scale-105'
                            : 'border-2'
                        }`}
                        style={depositMethod !== 'bank_transfer' ? {
                          background: 'rgba(0, 0, 0, 0.02)',
                          border: '2px solid rgba(0, 0, 0, 0.1)',
                          color: '#64748b'
                        } : {}}
                      >
                        <Banknote size={18} className={depositMethod === 'bank_transfer' ? 'text-white' : ''} style={depositMethod !== 'bank_transfer' ? { color: '#64748b' } : {}} />
                        Wire
                      </button>
                    </div>
                  </div>

                  {depositMethod === 'fiat' ? (
                    <div className="space-y-6">
                      <div className="rounded-xl p-6 border" style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(20, 184, 166, 0.05) 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.2)'
                      }}>
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
                            <DollarSign size={32} className="text-white" />
                          </div>
                          <h3 className="text-xl font-semibold mb-2" style={{ color: '#1e293b' }}>Fiat Deposit</h3>
                          <p className="mb-6" style={{ color: '#64748b' }}>
                            Deposit funds using EUR, USD, GBP, and other fiat currencies via our secure payment partner.
                          </p>
                          <div className="flex flex-wrap justify-center gap-3 mb-6">
                            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: 'rgba(0, 0, 0, 0.03)' }}>
                              <span className="text-lg">$</span>
                              <span style={{ color: '#1e293b' }}>USD</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: 'rgba(0, 0, 0, 0.03)' }}>
                              <span className="text-lg">&#8364;</span>
                              <span style={{ color: '#1e293b' }}>EUR</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: 'rgba(0, 0, 0, 0.03)' }}>
                              <span className="text-lg">&#163;</span>
                              <span style={{ color: '#1e293b' }}>GBP</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-center gap-2 text-sm mb-4" style={{ color: '#64748b' }}>
                            <Shield size={16} />
                            <span>Secure & Instant Processing</span>
                          </div>
                        </div>
                      </div>

                      <a
                        href="https://www.fundbridgepay.com/?b=COIN"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-4 rounded-xl font-semibold text-white text-center block transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
                          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)'
                        }}
                      >
                        <span className="flex items-center justify-center gap-2">
                          Deposit with Fiat
                          <ArrowRight size={18} />
                        </span>
                      </a>
                    </div>
                  ) : depositMethod === 'bank_transfer' ? (
                    <div className="space-y-6">
                      <div className="rounded-xl p-6 border" style={{
                        background: 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}>
                        <h3 className="text-lg font-semibold mb-4" style={{ color: '#1e293b' }}>Wire Transfer Details</h3>
                        {loadingBankDetails ? (
                          <div className="text-center py-8" style={{ color: '#64748b' }}>
                            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                            Loading bank details...
                          </div>
                        ) : error ? (
                          <div className="text-center py-8 text-red-500">
                            <AlertTriangle size={24} className="mx-auto mb-2" />
                            {error}
                          </div>
                        ) : bankDetails ? (
                          <div className="space-y-3">
                            <div>
                              <div className="text-sm" style={{ color: '#64748b' }}>Bank Name</div>
                              <div className="font-medium" style={{ color: '#1e293b' }}>{bankDetails.bank_name || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className="text-sm" style={{ color: '#64748b' }}>Account Number</div>
                              <div className="font-medium" style={{ color: '#1e293b' }}>{bankDetails.account_number || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className="text-sm" style={{ color: '#64748b' }}>Routing Number</div>
                              <div className="font-medium" style={{ color: '#1e293b' }}>{bankDetails.routing_number || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className="text-sm" style={{ color: '#64748b' }}>SWIFT/BIC Code</div>
                              <div className="font-medium" style={{ color: '#1e293b' }}>{bankDetails.swift_code || 'Not Yet Available'}</div>
                            </div>
                            <div>
                              <div className="text-sm" style={{ color: '#64748b' }}>Beneficiary Name</div>
                              <div className="font-medium" style={{ color: '#1e293b' }}>{bankDetails.beneficiary_name || 'Not Yet Available'}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8" style={{ color: '#64748b' }}>
                            <Banknote size={48} className="mx-auto mb-4 opacity-50" />
                            <p className="mb-2">Wire transfer details are not yet available for your account.</p>
                            <p className="text-sm">Please contact support for assistance.</p>
                          </div>
                        )}
                      </div>

                      <button
                        disabled={true}
                        className="w-full py-3 rounded-xl font-medium cursor-not-allowed"
                        style={{
                          background: 'rgba(0, 0, 0, 0.05)',
                          color: '#94a3b8'
                        }}
                      >
                        Deposit via Wire Transfer (Not Yet Available)
                      </button>
                    </div>
                  ) : (
                    userId ? (
                      <NowPaymentsDeposit
                        userId={userId}
                        onSuccess={() => {
                          setDepositMethod('fiat');
                        }}
                      />
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <p>Loading user information...</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="rounded-2xl p-6 border shadow-2xl" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <div className="flex items-start gap-4">
                <Info size={32} className="text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-2xl font-semibold mb-4" style={{ color: '#1e293b' }}>About Deposits</h3>
                  <p className="text-lg mb-6" style={{ color: '#1e293b', lineHeight: '1.6' }}>
                    All deposits are processed securely. For bank transfers, please ensure the beneficiary name matches your registered account name to avoid delays.
                  </p>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="rounded-lg p-5 border" style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                      <div className="mb-3 text-xl font-semibold" style={{ color: '#1e293b' }}>Processing Time</div>
                      <ul className="text-lg space-y-2" style={{ color: '#475569' }}>
                        <li>• Crypto: Instant</li>
                        <li>• Bank: 1-3 days</li>
                      </ul>
                    </div>
                    <div className="rounded-lg p-5 border" style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                      <div className="mb-3 text-xl font-semibold" style={{ color: '#1e293b' }}>Fees</div>
                      <ul className="text-lg space-y-2" style={{ color: '#475569' }}>
                        <li>• Crypto: Network fees</li>
                        <li>• Bank: No fees</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'withdraw' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="rounded-2xl p-8 border shadow-2xl" style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg" style={{
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
                  }}>
                    <ArrowUpRight size={20} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Withdraw Funds</h3>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Crypto Withdrawal Card */}
                    <button
                      onClick={() => handleCryptoWithdrawalClick('USDT')}
                      className="rounded-xl p-6 border transition-all duration-300 text-left hover:scale-[1.02]"
                      style={{
                        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
                        border: '1px solid rgba(239, 68, 68, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
                      }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
                        }}>
                          <Bitcoin size={24} className="text-white" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold" style={{ color: '#1e293b' }}>Crypto Withdrawal</h4>
                          <p className="text-sm" style={{ color: '#64748b' }}>USDT, BTC</p>
                        </div>
                      </div>
                      <p className="text-sm mb-4" style={{ color: '#64748b' }}>
                        Withdraw to your crypto wallet address. Supports multiple networks.
                      </p>
                      <div className="flex items-center gap-2 text-sm font-medium" style={{ color: '#ef4444' }}>
                        <span>Withdraw Crypto</span>
                        <ArrowRight size={16} />
                      </div>
                    </button>

                    {/* Bank Withdrawal Card */}
                    <button
                      onClick={() => setShowBankWithdrawalModal(true)}
                      className="rounded-xl p-6 border transition-all duration-300 text-left hover:scale-[1.02]"
                      style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)',
                        border: '1px solid rgba(59, 130, 246, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                      }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                        }}>
                          <Landmark size={24} className="text-white" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold" style={{ color: '#1e293b' }}>Bank Withdrawal</h4>
                          <p className="text-sm" style={{ color: '#64748b' }}>Wire Transfer</p>
                        </div>
                      </div>
                      <p className="text-sm mb-4" style={{ color: '#64748b' }}>
                        Withdraw directly to your bank account via wire transfer.
                      </p>
                      <div className="flex items-center gap-2 text-sm font-medium" style={{ color: '#3b82f6' }}>
                        <span>Withdraw to Bank</span>
                        <ArrowRight size={16} />
                      </div>
                    </button>
                  </div>

                  {/* Available Balance Display */}
                  <div className="rounded-xl p-6 border" style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-sm mb-1" style={{ color: '#64748b' }}>Available for Withdrawal</div>
                        <div className="text-2xl font-bold" style={{ color: '#1e293b' }}>
                          {showBalance ? formatCurrency(walletBreakdownData?.availableBalance || 0) : '••••••'}
                        </div>
                      </div>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.2)'
                      }}>
                        <Wallet size={24} style={{ color: '#22c55e' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="rounded-2xl p-6 border shadow-2xl" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <div className="flex items-start gap-4">
                <Info size={32} className="text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-2xl font-semibold mb-4" style={{ color: '#1e293b' }}>About Withdrawals</h3>
                  <p className="text-lg mb-6" style={{ color: '#1e293b', lineHeight: '1.6' }}>
                    Choose between crypto or bank withdrawals based on your preference. All withdrawals are processed securely.
                  </p>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="rounded-lg p-5 border" style={{
                      background: 'rgba(0, 0, 0, 0.02)',
                      border: '1px solid rgba(0, 0, 0, 0.08)'
                    }}>
                      <div className="mb-3 text-xl font-semibold" style={{ color: '#1e293b' }}>Processing Time</div>
                      <ul className="text-lg space-y-2" style={{ color: '#475569' }}>
                        <li>• Crypto: 10-30 minutes</li>
                        <li>• Bank: 1-3 business days</li>
                      </ul>
                    </div>
                    <div className="rounded-lg p-5 border" style={{
                      background: 'rgba(0, 0, 0, 0.02)',
                      border: '1px solid rgba(0, 0, 0, 0.08)'
                    }}>
                      <div className="mb-3 text-xl font-semibold" style={{ color: '#1e293b' }}>Withdrawal Fees</div>
                      <ul className="text-lg space-y-2" style={{ color: '#475569' }}>
                        <li>• Crypto: 0.1% + network fees</li>
                        <li>• Bank: 0.5%</li>
                      </ul>
                    </div>
                    <div className="rounded-lg p-5 border" style={{
                      background: 'rgba(0, 0, 0, 0.02)',
                      border: '1px solid rgba(0, 0, 0, 0.08)'
                    }}>
                      <div className="mb-3 text-xl font-semibold" style={{ color: '#1e293b' }}>Minimum Withdrawal</div>
                      <ul className="text-lg space-y-2" style={{ color: '#475569' }}>
                        <li>• USDT: 10 USDT</li>
                        <li>• BTC: 0.001 BTC</li>
                        <li>• Bank: 100 USDT</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="rounded-2xl p-8 border shadow-2xl" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Clock size={20} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Transaction History</h3>
            </div>

            <div className="space-y-3">
              {transactions.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-4 rounded-xl border transition-colors" style={{
                  background: '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="flex items-center gap-4">
                    {getTransactionIcon(transaction.type)}
                    <div>
                      <div className="font-medium" style={{ color: '#1e293b' }}>
                        {transaction.description}
                      </div>
                      <div className="text-sm" style={{ color: '#64748b' }}>
                        {new Date(transaction.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-bold ${
                      transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit'
                        ? 'text-green-600'
                        : transaction.type === 'withdrawal'
                        ? 'text-red-600'
                        : ''
                    }`} style={
                      transaction.type !== 'deposit' && transaction.type !== 'nowpayments_deposit' && transaction.type !== 'robot_profit' && transaction.type !== 'staking_profit' && transaction.type !== 'withdrawal'
                        ? { color: '#1e293b' }
                        : {}
                    }>
                      {transaction.type === 'deposit' || transaction.type === 'nowpayments_deposit' || transaction.type === 'robot_profit' || transaction.type === 'staking_profit' ? '+' : ''}
                      {formatCurrency(parseFloat(transaction.amount.toString()))}
                    </div>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(transaction.status)}
                      <span className={`text-sm ${getStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {transactions.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Clock size={64} className="mx-auto mb-4 opacity-50" />
                  <h4 className="text-xl font-medium mb-2">No transactions yet</h4>
                  <p>Your transaction history will appear here</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'staking' && (
          <div className="space-y-8">
            <div className="rounded-2xl p-6 border shadow-2xl" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Layers size={20} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Active Stakes</h3>
              </div>

              <div className="space-y-4">
                {userStakes.filter(stake => stake.status === 'active').map((stake) => {
                  const currentEarnings = calculateCurrentEarnings(stake);
                  const progress = ((Date.now() - new Date(stake.start_date).getTime()) / (new Date(stake.end_date).getTime() - new Date(stake.start_date).getTime())) * 100;
                  
                  return (
                    <div key={stake.id} className="rounded-xl p-4 border" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {stake.asset_symbol === 'BTC' ? (
                            <Bitcoin size={20} className="text-orange-400" />
                          ) : (
                            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-xs font-bold text-white">$</span>
                            </div>
                          )}
                          <span className="font-medium" style={{ color: '#1e293b' }}>{stake.asset_symbol}</span>
                        </div>
                        <span className="text-green-600 font-bold text-sm">
                          {stake.apy_rate}% APY
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <div className="text-xs" style={{ color: '#64748b' }}>Staked Amount</div>
                          <div className="font-medium" style={{ color: '#1e293b' }}>
                            {formatCrypto(parseFloat(stake.staked_amount.toString()), stake.asset_symbol)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs" style={{ color: '#64748b' }}>Current Earnings</div>
                          <div className="text-green-600 font-medium">
                            +{formatCrypto(currentEarnings, stake.asset_symbol)}
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1" style={{ color: '#64748b' }}>
                          <span>Progress</span>
                          <span>{Math.min(progress, 100).toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center text-xs" style={{ color: '#64748b' }}>
                        <span>End Date: {new Date(stake.end_date).toLocaleDateString()}</span>
                        <button
                          onClick={() => handleCancelStake(stake.id)}
                          className="text-red-600 hover:text-red-700 font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {userStakes.filter(stake => stake.status === 'active').length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Layers size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No active stakes</p>
                    <p className="text-sm">Start staking to earn passive income</p>
                  </div>
                )}
              </div>
            </div>

            {/* Staking Summary */}
            <div className="rounded-2xl p-6 border shadow-2xl" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              <h3 className="text-xl font-semibold mb-6" style={{ color: '#1e293b' }}>Staking Summary</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl p-4 border" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                  <div className="text-sm mb-1" style={{ color: '#64748b' }}>Total Staked Value</div>
                  <div className="text-2xl font-bold" style={{ color: '#1e293b' }}>
                    {showBalance ? formatCurrency(totalStakedValue) : '••••••'}
                  </div>
                </div>

                <div className="rounded-xl p-4 border" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                  <div className="text-sm mb-1" style={{ color: '#64748b' }}>Total Earnings</div>
                  <div className="text-2xl font-bold text-green-600">
                    {showBalance ? `+${formatCurrency(totalStakingEarnings)}` : '••••••'}
                  </div>
                </div>

                <div className="rounded-xl p-4 border" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
                  <div className="text-sm mb-1" style={{ color: '#64748b' }}>Active Stakes</div>
                  <div className="text-2xl font-bold" style={{ color: '#1e293b' }}>
                    {userStakes.filter(stake => stake.status === 'active').length}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => navigate(DASHBOARD_PATHS.staking)}
                className="w-full mt-6 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-3 rounded-xl font-medium transition-all duration-300 shadow-lg shadow-purple-500/25"
              >
                Start New Stake
              </button>
            </div>
          </div>
        )}

        {activeTab === 'holdings' && (
          <CryptoHoldings 
            usdtBalance={usdtBalance}
            btcBalance={btcBalance}
            currentBtcPrice={currentBtcPrice || 0}
            marketData={marketData}
            showBalance={showBalance}
          />
        )}
      </div>

      {/* Withdrawal Modal */}
      <BankWithdrawalModal 
        isOpen={showBankWithdrawalModal}
        onClose={() => setShowBankWithdrawalModal(false)}
        usdtBalance={walletBreakdownData?.availableBalance || 0}
        onWithdraw={handleBankWithdrawalSubmit} 
      />

      {/* Crypto Withdrawal Modal */}
      <CryptoWithdrawalModal
        isOpen={showCryptoWithdrawalModal}
        onClose={() => setShowCryptoWithdrawalModal(false)}
        usdtBalance={walletBreakdownData?.availableBalance || 0}
        btcBalance={btcBalance}
        currentBtcPrice={marketData.find(m => m.symbol === 'BTCUSDT')?.price || 0}
        initialCurrency={selectedCryptoForWithdrawal}
        onWithdraw={handleCryptoWithdrawal}
      />
    </div>
  );
};

export default WalletPage;
